import axios from 'axios';

// Configure axios to include credentials
axios.defaults.withCredentials = true;

// Use the proxy instead of the full URL
const API_URL = '/api';

/**
 * Get HubSpot contact by ID
 * @param {string} contactId - HubSpot contact ID
 * @param {string} source - Optional source identifier (e.g., 'bank-analysis') to allow sales access
 * @returns {Promise<Object>} Contact data
 */
export const getContactById = async (contactId, source = null) => {
  try {
    // Add source query param if provided (allows sales users for bank analysis)
    const url = source 
      ? `${API_URL}/hubspot/contacts/${contactId}?source=${source}`
      : `${API_URL}/hubspot/contacts/${contactId}`;
    
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error(`Error fetching HubSpot contact with ID ${contactId}:`, error);
    throw error;
  }
};

/**
 * Get HubSpot contact by email
 * @param {string} email - Contact email
 * @returns {Promise<Object>} Contact data
 */
export const getContactByEmail = async (email) => {
  try {
    const response = await axios.get(`${API_URL}/hubspot/contacts/email/${email}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching HubSpot contact with email ${email}:`, error);
    throw error;
  }
};

/**
 * Get mapped business data from HubSpot contact
 * @param {string} contactId - HubSpot contact ID
 * @returns {Promise<Object>} Mapped business data
 */
export const getMappedOpportunityData = async (contactId) => {
  try {
    const response = await axios.get(`${API_URL}/hubspot/contacts/${contactId}/opportunity`);
    return response.data;
  } catch (error) {
    console.error(`Error getting mapped business data for contact ${contactId}:`, error);
    throw error;
  }
};

/**
 * Updates a HubSpot contact with form data
 * @param {string} contactId - The HubSpot contact ID
 * @param {object} formData - The form data to update the contact with
 * @returns {Promise<object>} - The result of the update operation
 */
export const updateContactWithFormData = async (contactId, formData) => {
  try {
    console.log(`Updating HubSpot contact ${contactId} with form data:`, formData);
    
    const response = await axios.post(`${API_URL}/hubspot/contacts/${contactId}/update`, {
      formData
    });
    
    return response.data;
  } catch (error) {
    console.error('Error updating HubSpot contact:', error);
    throw error;
  }
};

/**
 * Alias for getMappedOpportunityData with more appropriate name
 * @param {string} contactId - HubSpot contact ID
 * @returns {Promise<Object>} Mapped business data
 */
export const getMappedBusinessData = getMappedOpportunityData;

/**
 * Get all files from a HubSpot contact's notes
 * @param {string} contactId - HubSpot contact ID
 * @returns {Promise<Object>} Response with files array
 */
export const getContactFiles = async (contactId) => {
  try {
    const response = await axios.get(`${API_URL}/hubspot/contacts/${contactId}/files`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching files for contact ${contactId}:`, error);
    throw error;
  }
};

export default {
  getContactById,
  getContactByEmail,
  getMappedOpportunityData,
  getMappedBusinessData,
  updateContactWithFormData,
  getContactFiles
}; 